import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Search, Star, Clock, Leaf, Flame, Plus, Minus, ShoppingCart, X, Check, Package, LayoutGrid, List, AlignJustify } from 'lucide-react';
import { menuService } from '../services/menuService';

// Scroll çubuğunu gizlemek için CSS
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

 const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const uiText = {
  tr: {
    loadingMenu: 'Menü yükleniyor...',
    restaurantNotFound: 'Restoran bulunamadı',
    useValidQr: 'Lütfen geçerli bir QR kod kullanın',
    allProductsSearch: 'Tüm ürünlerde ara...',
    listView: 'Liste görünümü (2 sütun)',
    gridView: 'Grid / resimli',
    simpleListView: 'Sade liste (ad ve fiyat)',
    categories: 'Kategoriler',
    searchResults: 'Arama Sonuçları',
    menuCategories: 'Menü Kategorileri',
    productNotFound: 'Ürün bulunamadı',
    tryDifferentFilter: 'Farklı bir kategori veya arama terimi deneyin',
    add: 'Ekle',
    loading: 'Yükleniyor...',
    cart: 'Sepetim',
    clearCart: 'Sepeti Temizle',
    emptyCart: 'Sepetiniz boş',
    optionalNotes: 'Notlar (İsteğe Bağlı)',
    submitOrder: 'Sipariş Ver',
    submitting: 'Gönderiliyor...',
    total: 'Toplam:',
    quantity: 'Adet:',
    addToCartWithQty: 'Sepete Ekle ({count} adet)',
    activeOrders: 'Aktif Siparişler',
    noOrderForStatus: 'Seçilen durumda sipariş bulunamadı',
    parentCategoriesTitle: 'Ana Kategoriler',
    allParentFilter: 'Tümü'
  },
  en: {
    loadingMenu: 'Loading menu...',
    restaurantNotFound: 'Restaurant not found',
    useValidQr: 'Please use a valid QR code',
    allProductsSearch: 'Search all products...',
    listView: 'Two-column list',
    gridView: 'Grid with images',
    simpleListView: 'Simple list (name & price)',
    categories: 'Categories',
    searchResults: 'Search Results',
    menuCategories: 'Menu Categories',
    productNotFound: 'No products found',
    tryDifferentFilter: 'Try another category or search term',
    add: 'Add',
    loading: 'Loading...',
    cart: 'My Cart',
    clearCart: 'Clear Cart',
    emptyCart: 'Your cart is empty',
    optionalNotes: 'Notes (Optional)',
    submitOrder: 'Place Order',
    submitting: 'Submitting...',
    total: 'Total:',
    quantity: 'Quantity:',
    addToCartWithQty: 'Add to cart ({count} pcs)',
    activeOrders: 'Active Orders',
    noOrderForStatus: 'No order found for selected status',
    parentCategoriesTitle: 'Parent categories',
    allParentFilter: 'All'
  }
};

/** Sepet kapalıysa false (API camelCase / PascalCase; string "false" vb. dahil). */
function isRestaurantCartEnabled(restaurant) {
  if (!restaurant) return false;
  const isOff = (v) =>
    v === false ||
    v === 0 ||
    (typeof v === 'string' && ['false', '0', 'no'].includes(String(v).trim().toLowerCase()));
  if (isOff(restaurant.isCartEnabled) || isOff(restaurant.IsCartEnabled)) return false;
  return true;
}

const CustomerMenuUI = () => {
  const { qrMenuId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, setLanguage] = useState(() => searchParams.get('lang') || localStorage.getItem('menuLanguage') || 'tr');
  const [qrCodeInfo, setQrCodeInfo] = useState(null); // QR kod bilgileri (restaurantId, tableNumber vb.)
  const [selectedParentCategory, setSelectedParentCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedProductQuantity, setSelectedProductQuantity] = useState(1);
  const [orderNotes, setOrderNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [activeOrders, setActiveOrders] = useState([]);
  const [showActiveOrder, setShowActiveOrder] = useState(false);
  const [selectedOrderIndex, setSelectedOrderIndex] = useState(0);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null); // null = Tümü
  const [viewMode, setViewMode] = useState(() => {
    const s = localStorage.getItem('menuViewMode');
    return s === 'grid' || s === 'list' || s === 'minimal' ? s : 'grid';
  });
  const [visibleCount, setVisibleCount] = useState(20);
  const categoryScrollRef = useRef(null);
  const categoryButtonRefs = useRef({});
  const productsListRef = useRef(null);
  const loadMoreRef = useRef(null);

  const t = (key, params = {}) => {
    const dictionary = uiText[language] || uiText.tr;
    const template = dictionary[key] || uiText.tr[key] || key;
    return template.replace(/\{(\w+)\}/g, (_, token) => params[token] ?? '');
  };

  useEffect(() => {
    const nextLang = language === 'en' ? 'en' : 'tr';
    localStorage.setItem('menuLanguage', nextLang);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('lang', nextLang);
      return next;
    }, { replace: true });
  }, [language, setSearchParams]);

  // QR kod ID ile menüyü yükle - tüm API çağrıları qrMenuId üzerinden çalışır
  useEffect(() => {
    const loadMenu = async () => {
      setLoading(true);
      try {
        console.log('Loading menu for QR code:', qrMenuId);

        // QR kod bilgisini çek (tableNumber vb. için)
        try {
          const qrData = await menuService.getQrCodeById(qrMenuId);
          console.log('QR Code data:', qrData);
          setQrCodeInfo(qrData);
        } catch (error) {
          console.warn("QR Code bilgisi alınamadı:", error);
        }

        // Restoran bilgilerini yükle (backend QR kod ID'yi çözümler)
        try {
          const restaurantData = await menuService.getRestaurant(qrMenuId, language);
          console.log('Restaurant data:', restaurantData);
          setRestaurant(restaurantData);
        } catch (error) {
          console.error("Error loading restaurant:", error);
          setRestaurant({
            id: qrMenuId,
            name: language === 'en' ? 'Restaurant' : 'Restoran',
            description: language === 'en' ? 'Delicious food here!' : 'Lezzetli yemekler burada!',
            primaryColor: "#FF6B35",
            logo: "🍽️"
          });
        }

        // Load categories (backend QR kod ID'yi çözümler)
        try {
          const categoriesData = await menuService.getCategories(qrMenuId, language);
          console.log('Categories data:', categoriesData);
          // Kategorileri displayOrder'a göre sırala
          const sortedCategories = categoriesData
            .map(cat => ({
              id: cat.id?.toString() || cat.id,
              name: cat.name,
              parentCategoryId: cat.parentCategoryId?.toString() || cat.ParentCategoryId?.toString() || '',
              icon: cat.icon || '🍽️',
              color: cat.color || 'bg-gray-100',
              image: cat.image || null,
              displayOrder: cat.displayOrder !== undefined && cat.displayOrder !== null ? cat.displayOrder : 999
            }))
            .sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
          setCategories(sortedCategories);
        } catch (error) {
          console.error("Error loading categories:", error);
          setCategories([]);
        }

        // Load products
        try {
          const productsData = await menuService.getProducts(qrMenuId, language);
          console.log('Products data:', productsData);
          setProducts(productsData.map(product => ({
            id: product.id?.toString() || product.id,
            categoryId: product.categoryId?.toString() || product.categoryId,
            name: product.name,
            description: product.description,
            price: product.price,
            image: product.image,
            isVegetarian: product.isVegetarian || false,
            isSpicy: product.isSpicy || false,
            preparationTime: product.preparationTime ,
            rating: product.rating || 4.5,
            ingredients: product.ingredients || '',
            calories: product.calories || 0,
            // Varyantları da kaydet (API'den geliyorsa)
            variants: (product.variants || product.Variants || []).map(variant => ({
              variantId: variant.variantId || variant.id || variant.VariantId,
              productId: variant.productId || variant.ProductId,
              name: variant.name || variant.Name,
              description: variant.description || variant.Description || '',
              priceModifier: variant.priceModifier || variant.PriceModifier || 0,
              displayOrder: variant.displayOrder || variant.DisplayOrder || 0,
              isDefault: variant.isDefault || variant.IsDefault || false,
              isActive: variant.isActive !== false && variant.IsActive !== false
            })).filter(v => v.isActive),
            // Eklentileri de kaydet (API'den geliyorsa)
            addons: (product.addons || product.Addons || []).map(addon => ({
              productId: addon.productId || addon.ProductId,
              addonId: addon.addonId || addon.AddonId,
              name: addon.name || addon.Name,
              description: addon.description || addon.Description || '',
              price: addon.price || addon.Price || 0,
              displayOrder: addon.displayOrder || addon.DisplayOrder || 0
            })).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
          })));
        } catch (error) {
          console.error("Error loading products:", error);
          setProducts([]);
        }

        // Load active orders
        try {
          const activeOrdersData = await menuService.getActiveOrders(qrMenuId);
          console.log('Active orders data from API:', activeOrdersData);
          
          // API'den gelen siparişleri frontend formatına çevir
          // Response formatı: ActiveOrdersResponseDto { orders: ActiveOrderDto[], totalAmount: decimal }
          // Veya direkt array olarak gelebilir: ActiveOrderDto[]
          const ordersArray = Array.isArray(activeOrdersData) 
            ? activeOrdersData 
            : (activeOrdersData?.orders || []);
          
          console.log('Orders array to process:', ordersArray);
          
          if (ordersArray.length > 0) {
            const formattedOrders = ordersArray.map(order => ({
              orderNumber: order.orderNumber || 'N/A',
              items: order.items.map(item => {
                // Her item için toplam fiyatı hesapla (API'den totalPrice geliyor ama yine de kontrol edelim)
                const variantPrice = item.variant ? parseFloat(item.variant.priceModifier || 0) : 0;
                const addonsPrice = (item.addons || []).reduce((sum, addon) => sum + parseFloat(addon.price || 0) * (addon.quantity || 1), 0);
                // API'den totalPrice geliyorsa onu kullan, yoksa hesapla
                const itemTotalPrice = item.totalPrice !== undefined ? parseFloat(item.totalPrice || 0) : (parseFloat(item.basePrice || 0) + variantPrice + addonsPrice);
                
                return {
                  id: item.productId?.toString() || item.productId, // Guid string olarak gelir
                  name: item.productName,
                  image: item.productImage || '',
                  price: itemTotalPrice,
                  quantity: item.quantity || 1,
                  variant: item.variant ? {
                    variantId: item.variant.variantId?.toString() || item.variant.variantId, // Guid string olarak gelir
                    name: item.variant.variantName,
                    priceModifier: parseFloat(item.variant.priceModifier || 0)
                  } : null,
                  addons: (item.addons || []).map(addon => ({
                    addonId: addon.addonId?.toString() || addon.addonId, // Guid string olarak gelir
                    name: addon.addonName,
                    price: parseFloat(addon.price || 0)
                  })),
                  cartItemId: `${item.productId}_${item.variant?.variantId || 'no-variant'}_${Date.now()}`,
                  basePrice: parseFloat(item.basePrice || 0),
                  variantPrice: variantPrice,
                  addonsPrice: addonsPrice
                };
              }),
              total: parseFloat(order.total || 0),
              notes: order.notes || '',
              createdAt: order.createdAt || new Date().toISOString(),
              status: order.status !== undefined ? order.status : 'Pending',
              statusDisplay: (order.statusDisplay && order.statusDisplay.trim() !== '') 
                            ? order.statusDisplay 
                            : (typeof order.status === 'number' 
                              ? (order.status === 0 ? 'Bekliyor' : 
                                 order.status === 1 ? 'Onaylandı' : 
                                 order.status === 2 ? 'Reddedildi' : 
                                 order.status === 3 ? 'Tamamlandı' : 
                                 order.status.toString())
                              : (order.status || 'Bekliyor'))
            }));
            setActiveOrders(formattedOrders);
            console.log('Active orders loaded:', formattedOrders);
          }
        } catch (error) {
          console.error("Error loading active orders:", error);
          // Hata durumunda boş bırak
        }
      } catch (error) {
        console.error("Unexpected error loading menu:", error);
      } finally {
        console.log('Loading completed');
        setLoading(false);
      }
    };

    if (qrMenuId) {
      loadMenu();
    } else {
      console.error('No qrMenuId provided');
      setLoading(false);
    }
  }, [qrMenuId, language]);

  const parentCategories = categories.filter(c => !c.parentCategoryId);
  const childCategories = categories.filter(c => c.parentCategoryId);
  const visibleCategories = selectedParentCategory
    ? categories.filter(c => c.parentCategoryId === selectedParentCategory)
    : (childCategories.length > 0 ? childCategories : categories);
  const isCartEnabled = isRestaurantCartEnabled(restaurant);
  const initialCategoryCards = selectedParentCategory
    ? visibleCategories
    : (parentCategories.length > 0 ? parentCategories : visibleCategories);
  const showTwoColumnParentCards =
    !selectedCategory &&
    !searchTerm &&
    !selectedParentCategory &&
    parentCategories.length > 0;

  const viewModeCycle = ['grid', 'list', 'minimal'];

  const toggleViewMode = () => {
    const i = viewModeCycle.indexOf(viewMode);
    const next = viewModeCycle[((i >= 0 ? i : 0) + 1) % viewModeCycle.length];
    setViewMode(next);
    localStorage.setItem('menuViewMode', next);
  };

  const getNextViewModeTitle = () => {
    if (viewMode === 'grid') return t('listView');
    if (viewMode === 'list') return t('simpleListView');
    return t('gridView');
  };

  // Seçili kategoriye scroll yap
  useEffect(() => {
    if (selectedCategory && categoryButtonRefs.current[selectedCategory]) {
      setTimeout(() => {
        const button = categoryButtonRefs.current[selectedCategory];
        if (button && categoryScrollRef.current) {
          button.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }, 100);
    }
  }, [selectedCategory]);

  // Kategori seçildiğinde sayfanın en üstüne scroll yap
  useEffect(() => {
    if (selectedCategory) {
      // Kısa bir gecikme ile scroll yap (DOM güncellemesi için)
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 50);
    }
  }, [selectedCategory]);

  // Kategori veya arama değişince görünen ürün sayısını sıfırla
  useEffect(() => {
    setVisibleCount(20);
  }, [selectedCategory, selectedParentCategory, searchTerm]);

  // Arama temizlenince search kategorisinden çık
  // Arama yazılınca "Arama Sonuçları" sekmesine geç, silinince ilk kategoriye geç
  useEffect(() => {
    if (searchTerm) {
      setSelectedCategory('search');
    } else if (selectedCategory === 'search') {
      setSelectedCategory(visibleCategories.length > 0 ? visibleCategories[0].id : '');
    }
  }, [searchTerm, visibleCategories, selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedParentCategory) return;
    const isSelectedCategoryVisible = visibleCategories.some(c => c.id === selectedCategory);
    if (!isSelectedCategoryVisible && selectedCategory !== 'search') {
      setSelectedCategory('');
    }
  }, [selectedParentCategory, visibleCategories, selectedCategory]);

  useEffect(() => {
    if (!selectedParentCategory || searchTerm) return;
    const firstChildCategory = categories.find(c => c.parentCategoryId === selectedParentCategory);
    if (firstChildCategory) {
      setSelectedCategory(firstChildCategory.id);
    }
  }, [selectedParentCategory, categories, searchTerm]);

  useEffect(() => {
    const enabled = isRestaurantCartEnabled(restaurant);
    if (restaurant && !enabled) {
      setCart([]);
      setShowCart(false);
      setShowActiveOrder(false);
    }
  }, [restaurant]);

  // Scroll ile daha fazla ürün yükle (IntersectionObserver)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => prev + 20);
        }
      },
      { threshold: 0.1 }
    );
    const el = loadMoreRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  });

  // Arama sonuçları - tüm kategorilerde arar
  const searchResults = searchTerm ? products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  const filteredProducts = selectedCategory === 'search'
    ? searchResults
    : products.filter(product => {
        const matchesCategory = !selectedCategory || product.categoryId === selectedCategory;
        const matchesSearch = !searchTerm ||
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
      });

  const visibleProducts = filteredProducts.slice(0, visibleCount);

  const openProductDetails = (product) => {
    setSelectedProduct(product);
    setSelectedVariant(null);
    setSelectedAddons([]);
    setSelectedProductQuantity(1);
  };

  const closeProductDetails = () => {
    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedAddons([]);
    setSelectedProductQuantity(1);
  };

  const toggleAddon = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => (a.addonId || a.id) === addon.addonId);
      if (exists) {
        return prev.filter(a => (a.addonId || a.id) !== addon.addonId);
      }
      return [...prev, addon];
    });
  };

  const addToCartFromModal = () => {
    if (!selectedProduct) return;

    // Varyant fiyatını hesapla
    const variantPrice = selectedVariant ? parseFloat(selectedVariant.priceModifier || 0) : 0;
    
    // Eklenti fiyatlarını hesapla
    const addonsPrice = selectedAddons.reduce((sum, addon) => sum + parseFloat(addon.price || 0), 0);
    
    // Toplam fiyat
    const totalPrice = parseFloat(selectedProduct.price) + variantPrice + addonsPrice;
    
    // Varyant ID'lerini karşılaştırmak için
    const variantId = selectedVariant?.variantId || null;
    
    // Eklenti ID'lerini sıralı olarak karşılaştırmak için
    const addonIds = selectedAddons.map(a => a.addonId || a.id).sort().join(',');
    
    setCart(prev => {
      // Aynı ürün, aynı varyant ve aynı eklentilerle sepette var mı kontrol et
      const existing = prev.find(item => {
        const itemVariantId = item.variant?.variantId || null;
        const itemAddonIds = item.addons?.map(a => a.addonId || a.id).sort().join(',') || '';
        
        return item.id === selectedProduct.id &&
               itemVariantId === variantId &&
               itemAddonIds === addonIds;
      });
      
      if (existing) {
        // Mevcut ürünün quantity'sini artır
        return prev.map(item => {
          const itemVariantId = item.variant?.variantId || null;
          const itemAddonIds = item.addons?.map(a => a.addonId || a.id).sort().join(',') || '';
          
          if (item.id === selectedProduct.id &&
              itemVariantId === variantId &&
              itemAddonIds === addonIds) {
            return { ...item, quantity: item.quantity + selectedProductQuantity };
          }
          return item;
        });
      } else {
        // Yeni ürün olarak ekle, quantity seçilen adet kadar
        const cartItem = {
          ...selectedProduct,
          price: totalPrice,
          variant: selectedVariant ? { 
            variantId: selectedVariant.variantId, 
            name: selectedVariant.name, 
            priceModifier: selectedVariant.priceModifier 
          } : null,
          addons: selectedAddons.map(a => ({ addonId: a.addonId, name: a.name, price: a.price })),
          cartItemId: `${selectedProduct.id}_${variantId || 'no-variant'}_${Date.now()}`,
          basePrice: parseFloat(selectedProduct.price),
          variantPrice: variantPrice,
          addonsPrice: addonsPrice,
          quantity: selectedProductQuantity
        };
        return [...prev, cartItem];
      }
    });
    
    closeProductDetails();
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && !item.variant && (!item.addons || item.addons.length === 0));
      if (existing) {
        return prev.map(item => 
          item.id === product.id && !item.variant && (!item.addons || item.addons.length === 0)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (cartItemId) => {
    setCart(prev => {
      const existing = prev.find(item => (item.cartItemId || item.id) === cartItemId);
      if (existing && existing.quantity > 1) {
        return prev.map(item => 
          (item.cartItemId || item.id) === cartItemId 
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter(item => (item.cartItemId || item.id) !== cartItemId);
    });
  };

  const clearCart = () => {
    if (window.confirm(language === 'en' ? 'Are you sure you want to clear the cart?' : 'Sepeti temizlemek istediğinize emin misiniz?')) {
      setCart([]);
      setOrderNotes('');
      setCustomerName('');
      setCustomerPhone('');
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getActiveOrdersTotal = () => {
    return activeOrders.reduce((total, order) => total + order.total, 0);
  };

  // Status değerini Türkçe'ye çevir
  const getStatusDisplay = (status) => {
    const isEn = language === 'en';
    if (typeof status === 'number') {
      switch(status) {
        case 0: return isEn ? 'Waiting Approval' : 'Onay Bekliyor';
        case 1: return isEn ? 'In Preparation' : 'Hazırlama Aşamasında';
        case 2: return isEn ? 'Rejected' : 'Reddedildi';
        case 3: return isEn ? 'Completed' : 'Tamamlandı';
        default: return status.toString();
      }
    }
    // String status değerleri için de kontrol et
    if (status === 'Pending' || status === '0' || status === 0) return isEn ? 'Waiting Approval' : 'Onay Bekliyor';
    if (status === 'Approved' || status === '1' || status === 1) return isEn ? 'In Preparation' : 'Hazırlama Aşamasında';
    return status || (isEn ? 'Waiting Approval' : 'Onay Bekliyor');
  };

  // Status'a göre filtreleme
  const getFilteredOrdersByStatus = () => {
    if (selectedStatusFilter === null) {
      return activeOrders;
    }
    return activeOrders.filter(order => {
      const orderStatus = order.status !== undefined ? order.status : 'Pending';
      // String veya number karşılaştırması
      if (typeof selectedStatusFilter === 'number' && typeof orderStatus === 'number') {
        return orderStatus === selectedStatusFilter;
      }
      if (typeof selectedStatusFilter === 'string' && typeof orderStatus === 'string') {
        return orderStatus === selectedStatusFilter;
      }
      // Farklı tipleri karşılaştır
      const filterDisplay = getStatusDisplay(selectedStatusFilter);
      const orderDisplay = order.statusDisplay || getStatusDisplay(orderStatus);
      return filterDisplay === orderDisplay;
    });
  };

  // Sadece Bekliyor (0) ve Onaylandı (1) status'lerini topla
  const getAllStatuses = () => {
    const statuses = new Set();
    activeOrders.forEach(order => {
      if (order.status !== undefined && order.status !== null) {
        const status = typeof order.status === 'number' ? order.status : order.status;
        // Sadece 0 (Bekliyor) ve 1 (Onaylandı) status'lerini ekle
        if (status === 0 || status === 1 || status === '0' || status === '1') {
          statuses.add(status);
        }
      }
    });
    return Array.from(statuses);
  };

  const submitOrder = async () => {
    if (cart.length === 0) return;

    // IsOutOrder true ise müşteri bilgileri zorunlu
    if (restaurant?.isOutOrder || restaurant?.IsOutOrder) {
      if (!customerName || !customerName.trim()) {
        alert(language === 'en' ? 'Please enter your full name.' : 'Lütfen ad soyad giriniz.');
        return;
      }
      if (!customerPhone || !customerPhone.trim()) {
        alert(language === 'en' ? 'Please enter your phone number.' : 'Lütfen telefon numarası giriniz.');
        return;
      }
    }

    setIsSubmittingOrder(true);
    try {
      // Sepet item'larını API formatına çevir
      const orderItems = cart.map(item => {
        const orderItem = {
          productId: item.id,
          quantity: item.quantity,
          notes: null,
          variantIds: item.variant ? [item.variant.variantId] : null,
          addons: item.addons && item.addons.length > 0 
            ? item.addons.map(addon => ({
                addonId: addon.addonId,
                quantity: 1
              }))
            : null
        };
        return orderItem;
      });

      // Sipariş DTO'sunu oluştur
      const isOutOrder = restaurant?.isOutOrder || restaurant?.IsOutOrder;
      const orderDto = {
        qrCodeId: qrMenuId, // URL'deki QR kod ID'si - backend bununla masa/oda bilgisini çözümler
        customerName: isOutOrder ? customerName : null,
        customerPhone: isOutOrder ? customerPhone.trim() : null,
        customerAddress: isOutOrder && customerAddress.trim() ? customerAddress.trim() : null,
        notes: orderNotes || null,
        items: orderItems
      };

      // API'ye gönder
      const response = await menuService.createOrder(orderDto);
      
      console.log('API Response:', response); // Debug için
      
      // Başarılı olursa - sepeti aktif siparişler listesine ekle
      const orderStatus = response.status !== undefined ? response.status : 'Pending';
      const orderData = {
        orderNumber: response.orderNumber || 'N/A',
        items: [...cart],
        total: getCartTotal(),
        notes: orderNotes || '',
        createdAt: new Date().toISOString(),
        status: orderStatus,
        statusDisplay: (response.statusDisplay && response.statusDisplay.trim() !== '') 
                      ? response.statusDisplay 
                      : (typeof orderStatus === 'number' 
                        ? (orderStatus === 0 ? 'Bekliyor' : 
                           orderStatus === 1 ? 'Onaylandı' : 
                           orderStatus === 2 ? 'Reddedildi' : 
                           orderStatus === 3 ? 'Tamamlandı' : 
                           orderStatus.toString())
                        : (orderStatus || 'Bekliyor'))
      };
      
      console.log('Order Data:', orderData); // Debug için
      
      // Yeni siparişi mevcut siparişler listesine ekle
      setActiveOrders(prev => [...prev, orderData]);
      
      // Başarılı mesajı
      alert(language === 'en'
        ? `Your order has been received! Order No: ${response.orderNumber || 'N/A'}`
        : `Siparişiniz alındı! Sipariş No: ${response.orderNumber || 'N/A'}`);
      
      // Sepeti temizle ve modal'ı kapat
      setCart([]);
      setShowCart(false);
      setOrderNotes('');
      setCustomerName('');
      setCustomerPhone('');
    } catch (error) {
      console.error('Sipariş verme hatası:', error);
      alert(language === 'en'
        ? 'An error occurred while placing your order. Please try again.'
        : 'Sipariş verilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  // Font Awesome class'larını emoji'ye çevir
  const getCategoryIcon = (iconClass) => {
    if (!iconClass) return '🍽️';
    
    const iconMap = {
      'fa-pizza-slice': '🍕',
      'fa-hamburger': '🍔',
      'fa-leaf': '🥗',
      'fa-utensils': '🍜',
      'fa-fish': '🍣',
      'fa-drumstick-bite': '🥩',
      'fa-bread-slice': '🥪',
      'fa-taco': '🌮',
      'fa-french-fries': '🍟',
      'fa-cake-candles': '🍰',
      'fa-ice-cream': '🍦',
      'fa-mug-hot': '☕',
      'fa-beer-mug-empty': '🍺',
      'fa-wine-glass': '🍷',
      'fa-bottle-water': '🥤',
      'fa-apple-whole': '🧃',
      'fa-smoking': '💨',
      'fa-cookie-bite': '🍪',
      'fa-circle': '🍩',
      'fa-candy-cane': '🍫',
      'fa-seedling': '🥜',
      'fa-carrot': '🥕',
      'fa-cheese': '🧀',
      'fa-egg': '🥚',
      'fa-square': '🧇',
      'fa-bacon': '🥓',
      'fa-hotdog': '🌭'
    };
    
    return iconMap[iconClass] || '🍽️';
  };

  // Kategori için resim URL'i oluştur
  const getCategoryImage = (category) => {
    // Önce API'den gelen image alanını kontrol et (null, undefined ve boş string kontrolü)
    if (category.image && category.image.trim() !== '') {
      // Eğer image tam URL ise direkt kullan, değilse API_BASE_URL ile birleştir
      if (category.image.startsWith('http://') || category.image.startsWith('https://')) {
        return category.image;
      }
      return `${API_BASE_URL}${category.image}`;
    }

    // API'den image gelmediyse placeholder resim kullan
    const categoryImages = {
      'En Çok Sevilenler': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=300&fit=crop',
      'Kahvaltılar': 'https://images.unsplash.com/photo-1525351484163-7529414344d8?w=400&h=300&fit=crop',
      'Tostlar': 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=400&h=300&fit=crop',
      'Çorbalar': 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=400&h=300&fit=crop',
      'Başlangıçlar & Atıştırmalıklar': 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop',
      'Bowl': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop',
      'Uzak Doğu': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop',
      'Salatalar': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop',
      'Fajitalar': 'https://images.unsplash.com/photo-1565299585323-38174c3c0b0e?w=400&h=300&fit=crop',
      'Rafinera Fit Lezzetler': 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop',
    };

    // Kategori adına göre resim bul veya genel bir resim kullan
    const categoryName = category.name || '';
    for (const [key, value] of Object.entries(categoryImages)) {
      if (categoryName.toLowerCase().includes(key.toLowerCase().split(' ')[0])) {
        return value;
      }
    }

    // Varsayılan resim
    return 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white">{t('loadingMenu')}</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-300 mb-2">{t('restaurantNotFound')}</h3>
          <p className="text-gray-500">{t('useValidQr')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <style>{scrollbarHideStyles}</style>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900 border-b border-gray-700">
        <div className="p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="h-12 w-12 sm:h-16 sm:w-16 flex-shrink-0 bg-gray-800 rounded-lg flex items-center justify-center overflow-hidden">
                {restaurant.logo ? (
                  <img
                    src={restaurant.logo.startsWith('http') ? restaurant.logo : `${API_BASE_URL}${restaurant.logo}`}
                    alt="Restaurant Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="text-2xl sm:text-3xl">🍽️</div>';
                    }}
                  />
                ) : (
                  <div className="text-2xl sm:text-3xl">🍽️</div>
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold truncate">{restaurant.name || 'Restoran'}</h1>
                <p className="text-gray-400 text-xs sm:text-sm truncate">{restaurant.description || 'Lezzetli yemekler burada!'}</p>
                {qrCodeInfo?.tableNumber && (
                  <p className="text-orange-400 text-xs font-medium">{qrCodeInfo.tableNumber}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-gray-800 text-white text-xs sm:text-sm px-2 py-1.5 rounded-lg border border-gray-700"
                aria-label="Language selector"
              >
                <option value="tr">TR</option>
                <option value="en">EN</option>
              </select>
              {isCartEnabled && (
                <button
                  onClick={() => {
                    if (activeOrders.length > 0) {
                      setSelectedOrderIndex(0);
                      setShowActiveOrder(true);
                    }
                  }}
                  className={`relative p-2 sm:px-3 sm:py-2 rounded-lg flex items-center space-x-1 ${
                    activeOrders.length > 0
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={activeOrders.length === 0}
                  title={activeOrders.length === 0 ? 'Aktif sipariş yok' : 'Aktif siparişleri görüntüle'}
                >
                  <Package className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline text-sm">Siparişler</span>
                  {activeOrders.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {activeOrders.length}
                    </span>
                  )}
                </button>
              )}
              {isCartEnabled && cart.length > 0 && (
                <button
                  onClick={() => setShowCart(true)}
                  className="relative bg-orange-600 text-white p-2 sm:px-3 sm:py-2 rounded-lg flex items-center space-x-1"
                >
                  <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {getCartItemCount()}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Search Bar + View Toggle - Her zaman göster */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={t('allProductsSearch')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-800 text-white pl-10 pr-4 py-3 rounded-xl border border-gray-700 focus:border-orange-500 focus:outline-none"
              />
            </div>
            {(selectedCategory || searchTerm) && (
              <button
                type="button"
                onClick={toggleViewMode}
                className="flex-shrink-0 bg-gray-800 text-gray-300 hover:text-white p-3 rounded-xl border border-gray-700 hover:border-orange-500 transition-colors"
                title={getNextViewModeTitle()}
                aria-label={getNextViewModeTitle()}
              >
                {viewMode === 'grid' ? <List className="h-5 w-5" /> : viewMode === 'list' ? <AlignJustify className="h-5 w-5" /> : <LayoutGrid className="h-5 w-5" />}
              </button>
            )}
          </div>

          {parentCategories.length > 0 && (
            <div className="mb-2">
              <p className="text-[10.5px] sm:text-[11.55px] text-gray-400 mb-1.5 leading-tight">{t('parentCategoriesTitle')}</p>
              <div className="flex space-x-1.5 overflow-x-auto pb-1.5 scrollbar-hide">
                <button
                  onClick={() => {
                    setSelectedParentCategory('');
                    setSelectedCategory('');
                    setSearchTerm('');
                  }}
                  className={`flex-shrink-0 px-[12px] py-[5px] rounded-[10px] text-[11.55px] font-bold leading-tight transition-colors ${
                    !selectedParentCategory ? 'bg-orange-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {t('allParentFilter')}
                </button>
                {parentCategories.map((parent) => (
                  <button
                    key={parent.id}
                    onClick={() => {
                      setSelectedParentCategory(parent.id);
                      setSelectedCategory('');
                      setSearchTerm('');
                    }}
                    className={`flex-shrink-0 px-[12px] py-[5px] rounded-[10px] text-[11.55px] font-bold leading-tight transition-colors ${
                      selectedParentCategory === parent.id ? 'bg-orange-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {parent.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div
            ref={categoryScrollRef}
            className="flex space-x-2 overflow-x-auto pb-1.5 scrollbar-hide"
          >
            <button
              onClick={() => {
                setSelectedCategory('');
                setSearchTerm('');
              }}
              className="flex-shrink-0 flex items-center space-x-1.5 px-[12px] py-[5px] rounded-[10px] text-[11.55px] font-bold leading-tight transition-colors bg-blue-600 text-white hover:bg-blue-700"
            >
              <span className="text-[12.6px] leading-none" aria-hidden>📋</span>
              <span className="whitespace-nowrap">{t('categories')}</span>
            </button>
            <button
              onClick={() => setSelectedCategory('search')}
              className={`flex-shrink-0 flex items-center space-x-1.5 px-[12px] py-[5px] rounded-[10px] text-[11.55px] font-bold leading-tight transition-colors ${
                selectedCategory === 'search'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span className="text-[12.6px] leading-none" aria-hidden>🔍</span>
              <span className="whitespace-nowrap">{t('searchResults')}</span>
            </button>
            {visibleCategories.map(category => (
              <button
                key={category.id}
                ref={(el) => {
                  if (el) {
                    categoryButtonRefs.current[category.id] = el;
                  }
                }}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex-shrink-0 flex items-center space-x-1.5 px-[12px] py-[5px] rounded-[10px] text-[11.55px] font-bold leading-tight transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="text-[12.6px] leading-none shrink-0">{getCategoryIcon(category.icon)}</span>
                <span className="whitespace-nowrap">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Categories Grid veya Products List */}
      <div className={`p-3 sm:p-4 ${isCartEnabled && cart.length > 0 ? 'pb-16 sm:pb-4' : ''}`}>
        {selectedCategory ? (
          // Products List - Kategori seçildiğinde göster
          <div ref={productsListRef}>
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🔍</div>
                <h3 className="text-lg font-medium text-gray-300 mb-2">{t('productNotFound')}</h3>
                <p className="text-gray-500">{t('tryDifferentFilter')}</p>
              </div>
            ) : viewMode === 'minimal' ? (
              // Tek sütun: sadece ad + fiyat
              <div className="flex flex-col rounded-lg border border-gray-700/80 overflow-hidden divide-y divide-gray-700/80">
                {visibleProducts.map(product => (
                  <button
                    type="button"
                    key={product.id}
                    onClick={() => openProductDetails(product)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left bg-gray-800 hover:bg-gray-700 active:bg-gray-600 transition-colors"
                  >
                    <span className="text-sm font-medium text-white line-clamp-2 min-w-0 flex-1">{product.name}</span>
                    <span className="text-sm font-bold text-orange-400 flex-shrink-0 tabular-nums">₺{product.price}</span>
                  </button>
                ))}
              </div>
            ) : viewMode === 'list' ? (
              // Kompakt Liste Görünümü (Market-uyumlu, 2 sütun, resimsiz)
              <div className="grid grid-cols-2 gap-2">
                {visibleProducts.map(product => (
                  <div
                    key={product.id}
                    onClick={() => openProductDetails(product)}
                    className="bg-gray-800 rounded-lg p-2.5 cursor-pointer active:bg-gray-700 transition-colors"
                  >
                    <h3 className="text-xs font-semibold text-white truncate">{product.name}</h3>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-sm font-bold text-orange-400">₺{product.price}</span>
                      {isCartEnabled && (
                        <div className="bg-orange-600 text-white p-1 rounded-md">
                          <Plus className="h-3.5 w-3.5" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Grid Görünümü (Kafe-uyumlu - mevcut tasarım)
              <div className="space-y-4">
                {visibleProducts.map(product => (
                  <div key={product.id} className="bg-gray-800 rounded-xl overflow-hidden flex flex-row sm:flex-col">
                    <div className="relative w-24 h-12 sm:w-full sm:h-24 flex-shrink-0 bg-gray-700 overflow-hidden">
                      <img
                        src={`${API_BASE_URL}${product.image}`}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute top-1 left-1 sm:top-2 sm:left-2 flex space-x-1">
                        {product.isVegetarian && (
                          <div className="bg-green-500 text-white p-1 rounded-full">
                            <Leaf className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          </div>
                        )}
                        {product.isSpicy && (
                          <div className="bg-red-500 text-white p-1 rounded-full">
                            <Flame className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-2.5 sm:p-4 flex-1 flex flex-col justify-between min-w-0">
                      <div>
                        <div className="flex justify-between items-start gap-2 mb-0.5 sm:mb-2">
                          <h3 className="text-sm sm:text-lg font-bold text-white line-clamp-1">{product.name}</h3>
                          <span className="text-sm sm:text-xl font-bold text-orange-400 flex-shrink-0">₺{product.price}</span>
                        </div>
                        <p className="text-gray-400 text-xs sm:text-sm line-clamp-2 mb-1.5 sm:mb-3">{product.description}</p>
                      </div>

                      {isCartEnabled && (
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => openProductDetails(product)}
                            className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                          >
                            {t('add')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Daha fazla ürün varsa yükleme tetikleyici */}
            {visibleCount < filteredProducts.length && (
              <div ref={loadMoreRef} className="flex justify-center py-4">
                <span className="text-gray-500 text-sm">{t('loading')}</span>
              </div>
            )}
          </div>
        ) : showTwoColumnParentCards ? (
          // Tümü seçiliyken ana kategoriler için 2'li kart görünümü
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-center text-white">{t('menuCategories')}</h3>
            {initialCategoryCards.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">📂</div>
                <p className="text-gray-400">{t('productNotFound')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {initialCategoryCards.map(category => (
                  <button
                    type="button"
                    key={category.id}
                    onClick={() => {
                      const isParentCard = !category.parentCategoryId && parentCategories.length > 0;
                      if (isParentCard) {
                        setSelectedParentCategory(category.id);
                        setSelectedCategory('');
                        setSearchTerm('');
                        return;
                      }
                      setSelectedCategory(category.id);
                    }}
                    className="relative h-32 sm:h-36 rounded-2xl overflow-hidden bg-gray-800 text-left active:scale-[0.99] transition-transform"
                  >
                    <img
                      src={getCategoryImage(category)}
                      alt={category.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                    <span className="absolute bottom-3 left-3 right-3 text-white text-base font-bold truncate">
                      {category.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Mobilde Sabit Alt Sepet Barı */}
      {isCartEnabled && cart.length > 0 && !showCart && !selectedProduct && !showActiveOrder && (
        <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden">
          <button
            onClick={() => setShowCart(true)}
            className="w-full bg-orange-600 text-white px-4 py-3 flex items-center justify-between shadow-lg"
          >
            <div className="flex items-center space-x-2">
              <ShoppingCart className="h-5 w-5" />
              <span className="font-medium">{getCartItemCount()} ürün</span>
            </div>
            <span className="font-bold">₺{getCartTotal().toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Cart Modal */}
      {isCartEnabled && showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-gray-800 w-full max-h-[85vh] rounded-t-2xl flex flex-col">
            <div className="p-4 border-b border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-bold">{t('cart')}</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              {cart.length > 0 && (
                <button
                  onClick={clearCart}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition-colors text-sm"
                >
                  {t('clearCart')}
                </button>
              )}
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">🛒</div>
                  <p className="text-gray-400">{t('emptyCart')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map(item => (
                    <div key={item.cartItemId || item.id} className="bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={`${API_BASE_URL}${item.image}`} 
                            alt={item.name} 
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div>
                            <h4 className="font-medium">{item.name}</h4>
                            {item.variant && (
                              <p className="text-xs text-gray-400">Varyant: {item.variant.name}</p>
                            )}
                            {item.addons && item.addons.length > 0 && (
                              <p className="text-xs text-gray-400">
                                Eklentiler: {item.addons.map(a => a.name).join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-400 font-semibold">₺{parseFloat(item.price).toFixed(2)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-end space-x-3">
                        <button
                          onClick={() => removeFromCart(item.cartItemId || item.id)}
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="font-medium">{item.quantity}</span>
                        <button
                          onClick={() => {
                            setCart(prev => 
                              prev.map(cartItem => 
                                (cartItem.cartItemId || cartItem.id) === (item.cartItemId || item.id)
                                  ? { ...cartItem, quantity: cartItem.quantity + 1 }
                                  : cartItem
                              )
                            );
                          }}
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {cart.length > 0 && (
              <div className="p-4 border-t border-gray-700 space-y-3 flex-shrink-0 overflow-y-auto max-h-[40vh]">
                {/* Müşteri Bilgileri - IsOutOrder true ise göster */}
                {(restaurant?.isOutOrder || restaurant?.IsOutOrder) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ad Soyad <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                        placeholder="Adınız ve soyadınız"
                        disabled={isSubmittingOrder}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Telefon Numarası <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                        placeholder="05XX XXX XX XX"
                        disabled={isSubmittingOrder}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Adres
                      </label>
                      <textarea
                        value={customerAddress}
                        onChange={(e) => setCustomerAddress(e.target.value)}
                        className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                        placeholder="Teslimat adresinizi girin..."
                        rows={3}
                        disabled={isSubmittingOrder}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('optionalNotes')}
                  </label>
                  <textarea
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                    placeholder="Özel istekleriniz..."
                    rows={3}
                    disabled={isSubmittingOrder}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">{t('total')}</span>
                  <span className="text-xl font-bold text-orange-400">₺{getCartTotal().toFixed(2)}</span>
                </div>
                <button 
                  onClick={submitOrder}
                  disabled={isSubmittingOrder}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {isSubmittingOrder ? t('submitting') : t('submitOrder')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
          <div className="bg-gray-800 w-full sm:max-w-2xl max-h-[90vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold">{selectedProduct.name}</h2>
              <button
                onClick={closeProductDetails}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto flex-1 p-4">
              {/* Product Image - liste görünümünde gizle */}
              {viewMode === 'grid' && (
                <div className="w-full h-16 sm:h-24 bg-gray-700 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                  <img
                    src={`${API_BASE_URL}${selectedProduct.image}`}
                    alt={selectedProduct.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              {/* Description */}
              <p className="text-gray-300 mb-4">{selectedProduct.description}</p>
              
              {/* Variants */}
              {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Varyant Seçin</h3>
                  <div className="space-y-2">
                    {selectedProduct.variants
                      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                      .map((variant) => {
                        const variantPrice = parseFloat(variant.priceModifier || 0);
                        const totalPrice = parseFloat(selectedProduct.price) + variantPrice;
                        const isSelected = selectedVariant?.variantId === variant.variantId;
                        
                        return (
                          <button
                            key={variant.variantId}
                            onClick={() => setSelectedVariant(variant)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                              isSelected 
                                ? 'border-orange-500 bg-orange-500/10' 
                                : 'border-gray-700 bg-gray-700 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'border-orange-500 bg-orange-500' : 'border-gray-500'
                                }`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                                <div>
                                  <div className="font-medium text-white">{variant.name}</div>
                                  {variant.description && (
                                    <div className="text-sm text-gray-400">{variant.description}</div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                {variantPrice !== 0 && (
                                  <div className={`text-sm ${variantPrice > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {variantPrice > 0 ? '+' : ''}₺{variantPrice.toFixed(2)}
                                  </div>
                                )}
                                <div className="text-orange-400 font-semibold">₺{totalPrice.toFixed(2)}</div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
              
              {/* Addons */}
              {selectedProduct.addons && selectedProduct.addons.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Eklentiler (İsteğe Bağlı)</h3>
                  <div className="space-y-2">
                    {selectedProduct.addons
                      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                      .map((addon) => {
                      const isSelected = selectedAddons.some(a => (a.addonId || a.id) === addon.addonId);
                      
                      return (
                        <button
                          key={addon.addonId}
                          onClick={() => toggleAddon(addon)}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                            isSelected 
                              ? 'border-orange-500 bg-orange-500/10' 
                              : 'border-gray-700 bg-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected ? 'border-orange-500 bg-orange-500' : 'border-gray-500'
                              }`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <div>
                                <div className="font-medium text-white">{addon.name}</div>
                                {addon.description && (
                                  <div className="text-sm text-gray-400">{addon.description}</div>
                                )}
                              </div>
                            </div>
                            <div className="text-orange-400 font-semibold">₺{parseFloat(addon.price || 0).toFixed(2)}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-700">
              {/* Adet Seçimi */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold">{t('quantity')}</span>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setSelectedProductQuantity(prev => Math.max(1, prev - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <Minus className="h-5 w-5 text-white" />
                  </button>
                  <span className="text-xl font-bold text-white min-w-[3rem] text-center">
                    {selectedProductQuantity}
                  </span>
                  <button
                    onClick={() => setSelectedProductQuantity(prev => prev + 1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <Plus className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold">{t('total')}</span>
                <span className="text-xl font-bold text-orange-400">
                  ₺{(() => {
                    const basePrice = parseFloat(selectedProduct.price);
                    const variantPrice = selectedVariant ? parseFloat(selectedVariant.priceModifier || 0) : 0;
                    const addonsPrice = selectedAddons.reduce((sum, addon) => sum + parseFloat(addon.price || 0), 0);
                    const unitPrice = basePrice + variantPrice + addonsPrice;
                    return (unitPrice * selectedProductQuantity).toFixed(2);
                  })()}
                </span>
              </div>
              {isCartEnabled && (
                <button
                  onClick={addToCartFromModal}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {t('addToCartWithQty', { count: selectedProductQuantity })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Order Modal */}
      {isCartEnabled && showActiveOrder && activeOrders.length > 0 && (() => {
        const filteredOrders = getFilteredOrdersByStatus();
        const allStatuses = getAllStatuses();
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-gray-800 w-full max-h-[80vh] rounded-t-2xl">
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-xl font-bold">{t('activeOrders')}</h2>
                  <p className="text-sm text-gray-400">
                    {selectedStatusFilter === null 
                      ? `${activeOrders.length} sipariş` 
                      : `${filteredOrders.length} sipariş (${getStatusDisplay(selectedStatusFilter)})`}
                  </p>
                  <p className="text-sm font-semibold text-orange-400 mt-1">
                    Genel Toplam: ₺{getActiveOrdersTotal().toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowActiveOrder(false);
                    setSelectedStatusFilter(null);
                    setSelectedOrderIndex(0);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {/* Status Filtreleme Butonları */}
              <div className="mb-3">
                <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
                  <button
                    onClick={() => {
                      setSelectedStatusFilter(null);
                      setSelectedOrderIndex(0);
                    }}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                      selectedStatusFilter === null
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <span className="text-sm font-medium">Tümü</span>
                  </button>
                  {allStatuses.map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setSelectedStatusFilter(status);
                        setSelectedOrderIndex(0);
                      }}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                        selectedStatusFilter === status
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      <span className="text-sm font-medium">{getStatusDisplay(status)}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Sipariş Seçimi */}
              {filteredOrders.length > 1 && (
                <div className="relative -mx-4 px-4">
                  <div 
                    className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide"
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      scrollBehavior: 'smooth'
                    }}
                  >
                    {filteredOrders.map((order, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          // Orijinal activeOrders dizisindeki index'ini bul
                          const originalIndex = activeOrders.findIndex(o => o.orderNumber === order.orderNumber);
                          setSelectedOrderIndex(originalIndex >= 0 ? originalIndex : index);
                        }}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                          activeOrders[selectedOrderIndex]?.orderNumber === order.orderNumber
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        <span className="text-sm font-medium">Sipariş #{order.orderNumber}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {filteredOrders.length > 0 && activeOrders[selectedOrderIndex] && (() => {
              const activeOrder = activeOrders[selectedOrderIndex];
              // Seçili sipariş filtreleme kapsamında değilse, filtreli siparişlerin ilkini göster
              const displayOrder = filteredOrders.some(o => o.orderNumber === activeOrder.orderNumber) 
                ? activeOrder 
                : filteredOrders[0];
              return (
                <>
                  <div className="p-4 border-b border-gray-700">
                    <p className="text-sm text-gray-400">Sipariş No: {displayOrder.orderNumber}</p>
                    {displayOrder.status !== undefined && displayOrder.status !== null && (
                      <p className="text-sm text-gray-300 mt-1">
                        Durum: {(displayOrder.statusDisplay && displayOrder.statusDisplay.trim() !== '') 
                                ? displayOrder.statusDisplay 
                                : getStatusDisplay(displayOrder.status)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(displayOrder.createdAt).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                    </p>
                  </div>
                  
                  <div className="p-4 overflow-y-auto max-h-96">
                    <div className="space-y-4">
                      {displayOrder.items.map((item, index) => (
                        <div key={item.cartItemId || item.id || index} className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <img 
                                src={`${API_BASE_URL}${item.image}`} 
                                alt={item.name} 
                                className="w-12 h-12 rounded-lg object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div>
                                <h4 className="font-medium">{item.name}</h4>
                                {item.variant && (
                                  <p className="text-xs text-gray-400">Varyant: {item.variant.name}</p>
                                )}
                                {item.addons && item.addons.length > 0 && (
                                  <p className="text-xs text-gray-400">
                                    Eklentiler: {item.addons.map(a => a.name).join(', ')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-orange-400 font-semibold">₺{parseFloat(item.price).toFixed(2)}</p>
                              <p className="text-xs text-gray-400">Adet: {item.quantity}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="p-4 border-t border-gray-700 space-y-4">
                    {displayOrder.notes && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Notlar
                        </label>
                        <div className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600">
                          {displayOrder.notes}
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold">Bu Sipariş Toplamı:</span>
                        <span className="text-xl font-bold text-orange-400">₺{displayOrder.total.toFixed(2)}</span>
                      </div>
                      {filteredOrders.length > 1 && (
                        <div className="flex justify-between items-center pt-2 border-t border-gray-600">
                          <span className="text-lg font-bold">
                            {selectedStatusFilter === null 
                              ? 'Tüm Siparişler Toplamı:' 
                              : `${getStatusDisplay(selectedStatusFilter)} Toplamı:`}
                          </span>
                          <span className="text-xl font-bold text-green-400">
                            ₺{filteredOrders.reduce((total, order) => total + order.total, 0).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {selectedStatusFilter !== null && activeOrders.length > filteredOrders.length && (
                        <div className="flex justify-between items-center pt-2 border-t border-gray-600">
                          <span className="text-lg font-bold">Tüm Siparişler Toplamı:</span>
                          <span className="text-xl font-bold text-blue-400">₺{getActiveOrdersTotal().toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
            {filteredOrders.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-4xl mb-4">📦</div>
                <p className="text-gray-400">{t('noOrderForStatus')}</p>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Floating Cart Button */}
      {isCartEnabled && cart.length > 0 && !showCart && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-orange-600 text-white p-4 rounded-full shadow-lg flex items-center space-x-2 z-40"
        >
          <ShoppingCart className="h-5 w-5" />
          <span className="font-medium">₺{getCartTotal()}</span>
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
            {getCartItemCount()}
          </span>
        </button>
      )}
    </div>
  );
};

export default CustomerMenuUI;